import type { JSX, ReactNode } from 'react';
import styles from './Alert.module.css';

export type AlertSeverity = 'info' | 'success' | 'warn' | 'danger';

export interface AlertProps {
  /** Visual severity. Default 'info'. */
  severity?: AlertSeverity;
  /** Optional bold title shown above the body. */
  title?: ReactNode;
  /** Body content. Required. */
  children: ReactNode;
  /** When provided, renders an X button that invokes this callback. */
  onDismiss?: () => void;
  /** Compact variant — tighter padding, smaller text. */
  compact?: boolean;
  /** Optional extra class. */
  className?: string;
  /** Optional leading icon — overrides the severity-default glyph. */
  icon?: ReactNode;
  /**
   * ARIA role override. Defaults: 'alert' for danger; 'status' for info/success/warn.
   * Use 'alert' for content the user must read immediately.
   */
  role?: 'alert' | 'status';
}

const DEFAULT_ICONS: Record<AlertSeverity, string> = {
  info: 'i',
  success: '✓',
  warn: '!',
  danger: '⚠',
};

/**
 * Inline alert / banner. Four severities × optional title × optional dismiss
 * × compact variant. Body never relies on color alone — there's always an
 * icon + text so color-blind users get the same signal.
 *
 * For transient feedback prefer the Toast component (future). For page-level
 * messages that are system-controlled, use Banner (future).
 */
export function Alert({
  severity = 'info',
  title,
  children,
  onDismiss,
  compact = false,
  className,
  icon,
  role,
}: AlertProps): JSX.Element {
  const resolvedRole = role ?? (severity === 'danger' ? 'alert' : 'status');

  const classes = [
    styles.alert,
    styles[`severity_${severity}`],
    compact ? styles.compact : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} role={resolvedRole}>
      <span className={styles.icon} aria-hidden="true">
        {icon ?? DEFAULT_ICONS[severity]}
      </span>

      <div className={styles.content}>
        {title && <p className={styles.title}>{title}</p>}
        <div className={styles.body}>{children}</div>
      </div>

      {onDismiss && (
        <button
          type="button"
          className={styles.dismiss}
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          ×
        </button>
      )}
    </div>
  );
}
